import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ReportConfig, LogbookEntry as LogbookEntryType } from './types';

// #773 — `position: 'absolute'` combined with `bottom` corrupts the layout of a
// `fixed` element once the report spans more than one page: pdfkit is handed a
// garbage translate coordinate and rejects it ("unsupported number:
// -1.8e+22"), failing the whole export. Anchoring from the top instead is
// equivalent here — the page size is fixed — and does not trigger the bug.
const A4_HEIGHT_PT = 841.89;
const FOOTER_INSET_PT = 20;
const FOOTER_LINE_HEIGHT_PT = 12;
const FOOTER_TOP_PT = A4_HEIGHT_PT - FOOTER_INSET_PT - FOOTER_LINE_HEIGHT_PT;

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    fontFamily: 'Helvetica',
    lineHeight: 1.5,
  },
  header: {
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#333',
    paddingBottom: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    fontFamily: 'Helvetica-Bold',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#555',
  },
  entry: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    borderBottomStyle: 'solid',
  },
  entryDate: {
    fontSize: 14,
    fontWeight: 'bold',
    fontFamily: 'Helvetica-Bold',
    marginBottom: 2,
  },
  entryAuthor: {
    fontSize: 11,
    fontStyle: 'italic',
    color: '#444',
    marginBottom: 8,
  },
  entryBody: {
    fontSize: 12,
    lineHeight: 1.5,
    textAlign: 'justify',
  },
  additionalContextLabel: {
    fontSize: 12,
    fontWeight: 'bold',
    fontFamily: 'Helvetica-Bold',
    marginTop: 10,
  },
  additionalContextBody: {
    fontSize: 12,
    lineHeight: 1.5,
    color: '#333',
    textAlign: 'justify',
  },
  pageNumber: {
    position: 'absolute',
    top: FOOTER_TOP_PT,
    right: 40,
    fontSize: 10,
    color: '#888',
  },
  footerText: {
    position: 'absolute',
    top: FOOTER_TOP_PT,
    left: 40,
    fontSize: 8,
    color: '#aaa',
  },
});

interface LogbookReportProps {
  config: ReportConfig;
}

// Entries stay wrappable. Marking them wrap={false} kept an entry from being
// split across a page, but once a report ran past one page the layout produced
// a garbage translate coordinate and pdfkit rejected it — "unsupported number:
// -1.8e+22" — taking down both PDF and PDF Legal export (#773). A split entry
// is a far smaller cost than no report at all.
const LogbookEntryView: React.FC<{ entry: LogbookEntryType; isLast: boolean }> = ({ entry, isLast }) => (
  <View style={isLast ? { ...styles.entry, borderBottomWidth: 0 } : styles.entry}>
    <Text style={styles.entryDate}>{entry.date}</Text>
    <Text style={styles.entryAuthor}>{entry.author}</Text>
    <Text style={styles.entryBody}>{entry.body}</Text>
    {entry.additionalContext && (
      <>
        <Text style={styles.additionalContextLabel}>Additional Context: </Text>
        <Text style={styles.additionalContextBody}>{entry.additionalContext}</Text>
      </>
    )}
  </View>
);

const LogbookReport: React.FC<LogbookReportProps> = ({ config }) => {
  const { title, subtitle, entries, branding, footer } = config;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header} fixed>
          {branding?.organizationName && (
            <Text style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>{branding.organizationName}</Text>
          )}
          <Text style={styles.title}>{title}</Text>
          {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
        </View>

        {entries.map((entry, index) => (
          <LogbookEntryView key={index} entry={entry} isLast={index === entries.length - 1} />
        ))}

        {footer?.confidentialityText && (
          <Text style={styles.footerText} fixed>
            {footer.confidentialityText}
          </Text>
        )}

        {(footer?.showPageNumbers !== false) && (
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            fixed
          />
        )}
      </Page>
    </Document>
  );
};

export default LogbookReport;
